/**
 * Story timeline — Liquid placeholders + theme editor live reload.
 */
(function () {
  function readTimelineData(root) {
    const dataEl = root.querySelector('[data-story-timeline-data]');
    if (!dataEl) return null;
    try {
      const data = JSON.parse(dataEl.textContent);
      return Array.isArray(data) && data.length ? data : null;
    } catch (e) {
      console.warn('[story-timeline] Invalid timeline data', e);
      return null;
    }
  }

  function initTimeline(root) {
    if (root.dataset.timelineInitialized === 'true') return;

    const storyGrid = root.querySelector('[data-role="story-grid"]');
    const rail = root.querySelector('[data-role="rail"]');
    const railPrev = root.querySelector('[data-role="prev"]');
    const railNext = root.querySelector('[data-role="next"]');

    if (!storyGrid || !rail || !railPrev || !railNext) return;

    let data = readTimelineData(root);
    if (!data) return;

    root.dataset.timelineInitialized = 'true';

    const getDesktopColumns = () => {
      const n = parseInt(root.dataset.columnsDesktop, 10);
      return Math.min(Math.max(Number.isFinite(n) ? n : 4, 3), 5);
    };

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const mobileQuery = window.matchMedia('(max-width: 720px)');

    let viewportStart = 0;
    let touchStartX = 0;
    let touchStartY = 0;
    let trackFrame = 0;
    let currentTrackMetrics = null;

    const escapeHtml = (value) =>
      String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const refreshData = () => {
      const next = readTimelineData(root);
      if (next) data = next;
      return data;
    };

    const buildStoryMedia = (item, globalIndex) => {
      const media = document.createElement('div');
      media.className = 'story-timeline__media';

      if (item.hasImage && item.image) {
        const img = document.createElement('img');
        img.className = 'story-timeline__story-image';
        img.src = item.image;
        img.alt = item.imageAlt || `${item.year} ${item.title}`;
        img.loading = 'lazy';
        if (item.imageWidth) img.width = item.imageWidth;
        if (item.imageHeight) img.height = item.imageHeight;
        media.appendChild(img);
        return media;
      }

      const tpl = root.querySelector(
        `template[data-story-media-fallback="${globalIndex}"]`
      );
      if (tpl?.content?.firstElementChild) {
        media.appendChild(tpl.content.firstElementChild.cloneNode(true));
      }

      return media;
    };

    const renderRail = () => {
      rail.innerHTML = `
        <div class="story-timeline__rail-canvas">
          <span class="story-timeline__rail-track" data-role="track-base"></span>
          <span class="story-timeline__rail-track-fill" data-role="track-fill"></span>
          <div class="story-timeline__rail-items">
            ${data
              .map(
                (item, index) => `
              <div class="story-timeline__item" data-index="${index}">
                <span class="story-timeline__dot">${index + 1}</span>
                <span class="story-timeline__item-copy">
                  <span class="story-timeline__year">${escapeHtml(item.year)}</span>
                  <span class="story-timeline__name">${escapeHtml(item.title)}</span>
                  <span class="story-timeline__note">${escapeHtml(item.nav)}</span>
                </span>
              </div>
            `
              )
              .join('')}
          </div>
        </div>
      `;
    };

    let railCanvas;
    let trackBase;
    let trackFill;
    let railItems;

    const bindRailRefs = () => {
      railCanvas = rail.querySelector('.story-timeline__rail-canvas');
      trackBase = rail.querySelector('[data-role="track-base"]');
      trackFill = rail.querySelector('[data-role="track-fill"]');
      railItems = rail.querySelector('.story-timeline__rail-items');
    };

    const getItems = () => Array.from(rail.querySelectorAll('.story-timeline__item'));

    renderRail();
    bindRailRefs();

    const getTimelineLayout = () => {
      const desktopColumns = getDesktopColumns();
      if (window.innerWidth <= 720) return { columns: 1, gap: 0 };
      if (window.innerWidth <= 900) return { columns: 2, gap: 16 };
      if (window.innerWidth <= 1120) return { columns: Math.min(3, desktopColumns), gap: 18 };
      return { columns: desktopColumns, gap: 18 };
    };

    const getVisibleCount = () => Math.min(getTimelineLayout().columns, data.length);
    const getMaxStart = () => Math.max(0, data.length - getVisibleCount());
    const clampViewportStart = (index) => Math.max(0, Math.min(index, getMaxStart()));

    const animateStories = () => {
      if (reduced) return;
      storyGrid.classList.remove('story-timeline__story-grid--animate');
      void storyGrid.offsetWidth;
      storyGrid.classList.add('story-timeline__story-grid--animate');
    };

    const renderStories = () => {
      refreshData();
      const visibleItems = data.slice(viewportStart, viewportStart + getVisibleCount());

      storyGrid.style.setProperty('--story-timeline-columns', String(getVisibleCount()));
      storyGrid.replaceChildren();

      visibleItems.forEach((item, localIndex) => {
        const globalIndex = viewportStart + localIndex;
        const article = document.createElement('article');
        article.className = 'story-timeline__story-card';

        article.appendChild(buildStoryMedia(item, globalIndex));

        const caption = document.createElement('div');
        caption.className = 'story-timeline__art-description';
        caption.innerHTML = `
        
          <p class="story-timeline__story-text">${escapeHtml(item.desc)}</p>
        `;
        article.appendChild(caption);

        storyGrid.appendChild(article);
      });
    };

    const getDotCenter = (dot, canvasRect) => {
      const rect = dot.getBoundingClientRect();
      return {
        x: rect.left + rect.width / 2 - canvasRect.left,
        y: rect.top + rect.height / 2 - canvasRect.top,
      };
    };

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const stopTrackAnimation = () => {
      if (!trackFrame) return;
      cancelAnimationFrame(trackFrame);
      trackFrame = 0;
    };

    const getTrackMetrics = () => {
      const list = getItems();
      if (!list.length) return null;

      const dots = list.map((button) => button.querySelector('.story-timeline__dot'));
      const firstDot = dots[0];
      const lastDot = dots[dots.length - 1];
      const endDot = dots[Math.min(viewportStart + getVisibleCount() - 1, dots.length - 1)];
      if (!firstDot || !lastDot || !endDot) return null;

      const canvasRect = railCanvas.getBoundingClientRect();
      const first = getDotCenter(firstDot, canvasRect);
      const last = getDotCenter(lastDot, canvasRect);
      const end = getDotCenter(endDot, canvasRect);

      return {
        baseLeft: first.x,
        baseTop: first.y - 1.5,
        baseWidth: Math.max(last.x - first.x, 0),
        fillLeft: first.x,
        fillTop: first.y - 2,
        fillWidth: Math.max(end.x - first.x, 0),
      };
    };

    const applyTrackMetrics = (metrics) => {
      trackBase.style.left = `${metrics.baseLeft}px`;
      trackBase.style.top = `${metrics.baseTop}px`;
      trackBase.style.width = `${metrics.baseWidth}px`;
      trackBase.style.height = '3px';

      trackFill.style.opacity = '1';
      trackFill.style.left = `${metrics.fillLeft}px`;
      trackFill.style.top = `${metrics.fillTop}px`;
      trackFill.style.width = `${metrics.fillWidth}px`;
      trackFill.style.height = '4px';
    };

    const syncTrack = (animated = false) => {
      const nextMetrics = getTrackMetrics();
      if (!nextMetrics) return;

      stopTrackAnimation();

      if (!animated || !currentTrackMetrics) {
        applyTrackMetrics(nextMetrics);
        currentTrackMetrics = nextMetrics;
        return;
      }

      trackBase.style.left = `${nextMetrics.baseLeft}px`;
      trackBase.style.top = `${nextMetrics.baseTop}px`;
      trackBase.style.width = `${nextMetrics.baseWidth}px`;
      trackBase.style.height = '3px';
      trackFill.style.opacity = '1';
      trackFill.style.height = '4px';

      const fromMetrics = { ...currentTrackMetrics };
      const duration = 1400;
      let startTime = 0;

      const frame = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = easeOutCubic(progress);

        const fillLeft = fromMetrics.fillLeft + (nextMetrics.fillLeft - fromMetrics.fillLeft) * eased;
        const fillTop = fromMetrics.fillTop + (nextMetrics.fillTop - fromMetrics.fillTop) * eased;
        const fillWidth = fromMetrics.fillWidth + (nextMetrics.fillWidth - fromMetrics.fillWidth) * eased;

        trackFill.style.left = `${fillLeft}px`;
        trackFill.style.top = `${fillTop}px`;
        trackFill.style.width = `${fillWidth}px`;
        currentTrackMetrics = { ...nextMetrics, fillLeft, fillTop, fillWidth };

        if (progress < 1) {
          trackFrame = requestAnimationFrame(frame);
          return;
        }

        trackFrame = 0;
        applyTrackMetrics(nextMetrics);
        currentTrackMetrics = nextMetrics;
      };

      trackFrame = requestAnimationFrame(frame);
    };

    const updateTimelineState = () => {
      const visibleEnd = viewportStart + getVisibleCount() - 1;

      getItems().forEach((button, index) => {
        const inView = index >= viewportStart && index <= visibleEnd;
        button.classList.toggle('story-timeline__item--active', inView);
      });
    };

    const updateControls = () => {
      railPrev.disabled = viewportStart === 0;
      railNext.disabled = viewportStart >= getMaxStart();
    };

    const scrollRailToStart = (animated = true) => {
      const target = getItems()[viewportStart];
      if (!target) return;

      rail.scrollTo({
        left: Math.max(target.offsetLeft - 4, 0),
        behavior: animated && !reduced && mobileQuery.matches ? 'smooth' : 'auto',
      });
    };

    const layoutRail = () => {
      const list = getItems();
      if (!list.length) return;

      const { columns, gap } = getTimelineLayout();
      const railWidth = rail.clientWidth;
      const itemWidth =
        columns === 1 ? railWidth : Math.max((railWidth - gap * (columns - 1) - 8) / columns, 0);
      const totalWidth = itemWidth * list.length + gap * Math.max(list.length - 1, 0);

      railItems.style.gap = `${gap}px`;
      list.forEach((button) => {
        button.style.setProperty('--story-timeline-item-width', `${itemWidth}px`);
      });
      railCanvas.style.width = `${Math.max(railWidth, totalWidth + 8)}px`;
    };

    const renderView = (animated = false) => {
      refreshData();
      viewportStart = clampViewportStart(viewportStart);

      if (getItems().length !== data.length) {
        renderRail();
        bindRailRefs();
      }

      renderStories();
      if (animated) {
        animateStories();
      } else {
        storyGrid.classList.remove('story-timeline__story-grid--animate');
      }
      updateTimelineState();
      updateControls();
      layoutRail();
      requestAnimationFrame(() => {
        syncTrack(animated);
        scrollRailToStart(animated);
      });
    };

    const onResize = () => renderView(false);

    railPrev.addEventListener('click', () => {
      viewportStart = clampViewportStart(viewportStart - 1);
      renderView(true);
    });

    railNext.addEventListener('click', () => {
      viewportStart = clampViewportStart(viewportStart + 1);
      renderView(true);
    });

    storyGrid.addEventListener(
      'touchstart',
      (event) => {
        if (!mobileQuery.matches || !event.changedTouches.length) return;
        const touch = event.changedTouches[0];
        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
      },
      { passive: true }
    );

    storyGrid.addEventListener(
      'touchend',
      (event) => {
        if (!mobileQuery.matches || !event.changedTouches.length) return;

        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - touchStartX;
        const deltaY = touch.clientY - touchStartY;
        const horizontalIntent = Math.abs(deltaX) > 48 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2;

        if (!horizontalIntent) return;

        viewportStart =
          deltaX < 0
            ? clampViewportStart(viewportStart + 1)
            : clampViewportStart(viewportStart - 1);

        renderView(true);
      },
      { passive: true }
    );

    window.addEventListener('resize', onResize);
    mobileQuery.addEventListener('change', onResize);

    root.__storyTimelineRender = renderView;
    renderView(false);
  }

  function initTimelines(scope) {
    const container = scope || document;
    container.querySelectorAll('[data-story-timeline]').forEach(initTimeline);
  }

  function reloadTimelineSection(event) {
    const sectionRoot = event.target;
    if (!sectionRoot?.querySelector?.('[data-story-timeline]')) return;

    initTimelines(sectionRoot);
  }

  initTimelines(document);

  if (!window.__STORY_TIMELINE_EVENTS__) {
    window.__STORY_TIMELINE_EVENTS__ = true;
    document.addEventListener('shopify:section:load', reloadTimelineSection);
  }
})();
